import { useRef, useState, useEffect, useCallback } from "react";
import { EventDetailsCard } from "@shared/modules/events/components/event-details/EventDetailsCard/EventDetailsCard.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { useTranslation } from "@shared/utils/internationalization";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";

/** Target width for each card in pixels */
const DEFAULT_CARD_WIDTH = 300;
/** Maximum width for each card in pixels */
const DEFAULT_MAX_CARD_WIDTH = 400;
/** Minimum number of cards to show */
const MIN_CARDS = 1;
/** Maximum number of cards to show */
const MAX_CARDS = 6;
/** Gap between cards in pixels */
const CARD_GAP = 16;

export interface UpcomingEventsSliderProps {
  upcomingEvents: PublicBaseEventSerializer[];
  /**
   * Target width for each card. The component will calculate how many
   * cards fit based on its container width.
   * @default 300
   */
  cardWidth?: number;
  /**
   * Maximum width for each card. Cards won't grow beyond this size.
   * @default 400
   */
  maxCardWidth?: number;
  /**
   * Minimum number of cards to show regardless of container width.
   * @default 1
   */
  minCards?: number;
  /**
   * Maximum number of cards to show regardless of container width.
   * @default 6
   */
  maxCards?: number;
}

/**
 * Swiper container styles for the upcoming events slider.
 * Uses css prop for third-party library integration.
 */
const swiperContainerStyles = {
  "& .swiper-container": {
    maxWidth: "100% !important",
    width: "100% !important",
    height: "auto",
  },
  "& .swiper-slide": {
    height: "auto",
    padding: "3px",
  },
  "& .swiper-button-prev": {
    color: "fg",
  },
  "& .swiper-button-next": {
    color: "fg",
  },
};

export const UpcomingEventsSlider: React.FC<UpcomingEventsSliderProps> = ({
  upcomingEvents,
  cardWidth = DEFAULT_CARD_WIDTH,
  maxCardWidth = DEFAULT_MAX_CARD_WIDTH,
  minCards = MIN_CARDS,
  maxCards = MAX_CARDS,
}) => {
  // Needed for auto-swiping event cards on mobile
  SwiperCore.use([Navigation, Autoplay]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [slidesPerView, setSlidesPerView] = useState(minCards);

  // Calculate how many cards fit based on container width
  const calculateSlidesPerView = useCallback(
    (containerWidth: number): number => {
      // Account for nav buttons (~80px total) and gaps
      const availableWidth = containerWidth - 80;
      const cardWithGap = cardWidth + CARD_GAP;
      const calculatedCards = Math.floor(availableWidth / cardWithGap);

      // Clamp between min and max
      return Math.max(minCards, Math.min(maxCards, calculatedCards));
    },
    [cardWidth, minCards, maxCards]
  );

  // Observe container size changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(({ contentRect }) => {
        setSlidesPerView(calculateSlidesPerView(contentRect.width));
      });
    });

    resizeObserver.observe(container);

    // Initial calculation
    setSlidesPerView(calculateSlidesPerView(container.offsetWidth));

    return () => resizeObserver.disconnect();
  }, [calculateSlidesPerView]);

  const { t } = useTranslation("common");

  const getEventCard = (event: PublicBaseEventSerializer): JSX.Element => {
    return (
      <EventDetailsCard
        isLoading={false}
        event={event}
        tracking_label_suffix="upcoming-events-slider"
      />
    );
  };

  return (
    <Box ref={containerRef} width="100%">
      {/* Single upcoming event displayed without swiper */}
      {upcomingEvents.length === 1 && (
        <Flex justifyContent="center" width="100%" px="4xl">
          <Box width="100%" style={{ maxWidth: maxCardWidth }}>
            {getEventCard(upcomingEvents[0])}
          </Box>
        </Flex>
      )}

      {/* Multiple upcoming events displayed with swiper */}
      {upcomingEvents.length > 1 && (
        <Box height="auto" alignItems="center" css={swiperContainerStyles}>
          <Flex height="auto" alignItems="center">
            <IconButton
              iconName="ChevronLeft"
              iconOutline={false}
              iconColor="font.dark"
              iconSize="20px"
              className="prev"
              aria-label={t("Previous event")}
              colorPalette="light"
            />
            <Swiper
              navigation={{
                prevEl: ".prev",
                nextEl: ".next",
              }}
              loop={upcomingEvents.length > slidesPerView}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              spaceBetween={CARD_GAP}
              slidesPerView={slidesPerView}
            >
              {upcomingEvents.map((event: PublicBaseEventSerializer) => (
                <SwiperSlide key={event.slug}>
                  {/* Consistent card sizing with max width constraint */}
                  <Flex justifyContent="center" width="100%" height="100%">
                    <Box
                      width="100%"
                      height="100%"
                      style={{ maxWidth: maxCardWidth }}
                    >
                      {getEventCard(event)}
                    </Box>
                  </Flex>
                </SwiperSlide>
              ))}
            </Swiper>
            <IconButton
              iconName="ChevronRight"
              iconOutline={false}
              iconColor="font.dark"
              iconSize="20px"
              className="next"
              aria-label={t("Next event")}
              colorPalette="light"
            />
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default UpcomingEventsSlider;
